import { Mutex } from 'async-mutex'
import * as oauth2 from 'oauth4webapi'

import { Session, storeSession, loadSession } from './session'
import { getClient, getServer } from './serverClient'
import { logout } from './loginLogout'
import { AuthSettings } from './types'

const refreshTokenMutex = new Mutex()

export const getAccessToken = (authSettings: AuthSettings) => async () => {
    const session = await refreshTokenIfNeeded(authSettings)
    return session?.accessToken || null
}

const isExpired = (session: Session) => {
    if (!session?.tokenExpiration) {
        return false
    }

    const expiration = new Date(session.tokenExpiration)

    return expiration.getTime() - 10000 < Date.now()
}

export const refreshTokenIfNeeded = async (authSettings: AuthSettings): Promise<Session | null> => {
    return refreshTokenMutex.runExclusive<Promise<Session | null>>(async () => {
        const { issuer, clientId } = authSettings

        const session = authSettings.getSession ? await authSettings.getSession() : loadSession(authSettings)

        if (!session) {
            return null
        }

        if (!isExpired(session)) {
            return session
        }

        if (!isExpired(session) || !session.refreshToken) {
            return session
        }

        const server = getServer(issuer)
        const client = getClient(clientId)

        const response = await oauth2.refreshTokenGrantRequest(server, client, session.refreshToken)
        const result = await oauth2.processRefreshTokenResponse(server, client, response)

        if (oauth2.isOAuth2Error(result)) {
            console.log('error refreshing token', result)

            logout(authSettings, session)({ onlyClearSession: true })
            window.location.reload()

            // Wait to stop code execution since we're reloading anyway
            await new Promise((resolve) => setTimeout(resolve, 5000))

            return null
        }

        const { access_token, refresh_token, expires_in = 0, id_token } = result

        const newSession: Session = {
            ...session,
            idToken: id_token || session.idToken,
            accessToken: access_token,
            refreshToken: refresh_token,
            tokenExpiration: new Date(Date.now() + expires_in * 1000),
        }

        storeSession(authSettings, newSession)

        return newSession
    })
}
